import * as d3 from 'd3';

function GaugeChart() {
  const pi = Math.PI;
  const rad = pi / 180;
  const deg = 180 / pi;

  const properties = {
    width: 700,
    height: 400,
    margin: 100,

    rotation: 0,
    thickness: 0.1,
    arc: 1,
    ticks: 5,
    extraTicks: [],

    color_threshold: [
      { color: '#f44336', finishStep: 25 },
      { color: '#ffc107', finishStep: 75 },
      { color: '#4ea96d', finishStep: 100 }
    ],
    color_step: 4,
    tick_color: '#FFFFFF',
    needle_color: '#000000'
  };

  let needlePercent = 0;
  const center = {};
  const radii = {};
  const angles = {};
  let ticks = {};
  let gradient = [];
  const scales = {};

  const setCenter = (function initCenter() {
    center.x = properties.width / 2;
    center.y = properties.height - 1.5 * properties.margin;

    return initCenter;
  })();

  const setRadii = (function initRadii() {
    const base = properties.height - 2 * properties.margin;

    radii.base = base;
    radii.cap = base / 15;
    radii.inner = base * (1 - properties.thickness);
    radii.outer_tick = base + 5;
    radii.tick_label = base + 15;

    return initRadii;
  })();

  const setAngles = (function initAngles() {
    const arcComplement = 1 - properties.arc;

    angles.arc_complement = arcComplement;
    angles.start_angle = -pi / 2 + (pi * arcComplement) / 2 + properties.rotation * rad;
    angles.end_angle = pi / 2 - (pi * arcComplement) / 2 + properties.rotation * rad;

    return initAngles;
  })();

  const setTicks = (function initTicks() {
    const subArc = (angles.end_angle - angles.start_angle) / (properties.ticks - 1);
    const tickPct = 100 / (properties.ticks - 1);

    ticks = d3.range(properties.ticks).map(function(d) {
      const subAngle = angles.start_angle + subArc * d;
      return {
        label: (tickPct * d).toFixed(0) + '',
        angle: subAngle,
        coordinates: [
          [subAngle, radii.inner],
          [subAngle, radii.outer_tick]
        ]
      };
    });

    if (properties.extraTicks[0]) {
      const value = properties.extraTicks[0] / 100;
      const subAngle = angles.start_angle + subArc * (value * (properties.ticks - 1));
      const test = {
        label: (100 * value).toFixed(0) + '',
        angle: subAngle,
        coordinates: [
          [subAngle, radii.inner],
          [subAngle, radii.outer_tick]
        ]
      };
      ticks[1] = test;
    }

    if (properties.extraTicks[1]) {
      const value2 = properties.extraTicks[1] / 100;
      const subAngle2 = angles.start_angle + subArc * (value2 * (properties.ticks - 1));
      const test2 = {
        label: (100 * value2).toFixed(0) + '',
        angle: subAngle2,
        coordinates: [
          [subAngle2, radii.inner],
          [subAngle2, radii.outer_tick]
        ]
      };

      ticks[2] = test2;
    }

    return initTicks;
  })();

  const setGradient = (function initGradient() {
    const samples = properties.color_step;
    const totalArc = angles.end_angle - angles.start_angle;
    const subArc = totalArc / samples;

    let currentColorIndex = 0;

    gradient = d3.range(samples).map(function(step) {
      if (step >= properties.color_threshold[currentColorIndex].finishStep) {
        currentColorIndex++;
      }

      const subStartAngle = angles.start_angle + subArc * step;
      const subEndAngle = subStartAngle + subArc;

      return {
        fill: properties.color_threshold[currentColorIndex].color,
        start: subStartAngle,
        end: subEndAngle
      };
    });

    return initGradient;
  })();

  const setScales = (function initScales() {
    scales.lineRadial = d3.lineRadial();

    scales.subArcScale = d3
      .arc()
      .innerRadius(radii.inner + 1)
      .outerRadius(radii.base)
      .startAngle(d => d.start)
      .endAngle(d => d.end);

    scales.needleScale = d3
      .scaleLinear()
      .domain([0, 1])
      .range([angles.start_angle, angles.end_angle]);

    return initScales;
  })();

  function updateValues() {
    setCenter();
    setRadii();
    setAngles();
    setTicks();
    setGradient();
    setScales();
  }

  const GaugeChart = {};

  GaugeChart.setProperties = function(params) {
    Object.keys(params).forEach(function(d) {
      if (d in properties) {
        properties[d] = params[d];
      } else {
        throw new Error('One or more parameters not accepted.');
      }
    });
    updateValues();
  };

  GaugeChart.getProperties = function() {
    return properties;
  };

  GaugeChart.setPercentage = function(pct) {
    needlePercent = pct;
  };

  GaugeChart.draw = function() {
    const svg = d3.create('svg').attr('viewBox', [0, 0, properties.width, properties.height]);

    const gauge = svg
      .append('g')
      .attr('transform', `translate(${center.x}, ${center.y})`)
      .attr('class', 'gauge-container');

    gauge
      .append('g')
      .attr('class', 'gauge-arc')
      .selectAll('path')
      .data(gradient)
      .enter()
      .append('path')
      .attr('d', scales.subArcScale)
      .attr('fill', d => d.fill)
      .attr('stroke-width', 0.5)
      .attr('stroke', d => d.fill);

    gauge
      .append('g')
      .attr('class', 'gauge-ticks')
      .selectAll('path')
      .data(ticks)
      .enter()
      .append('g')
      .attr('class', 'tick')
      .append('path')
      .attr('d', d => scales.lineRadial(d.coordinates))
      .attr('stroke', properties.tick_color)
      .attr('stroke-width', 2)
      .attr('stroke-linecap', 'round')
      .attr('fill', 'none');

    gauge
      .select('g.gauge-ticks')
      .selectAll('text')
      .data(ticks)
      .enter()
      .append('g')
      .attr('class', 'tick-label')
      .append('text')
      .attr(
        'transform',
        d =>
          `translate(${radii.tick_label * Math.sin(d.angle)},
                       ${-radii.tick_label * Math.cos(d.angle) - 10})
              rotate(${d.angle * deg - pi})`
      )
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', '0.67em')
      .attr('fill', 'var(--v-surface-lighten4)')
      .text(d => d.label + '%');

    gauge
      .append('g')
      .attr('class', 'needle')
      .selectAll('path')
      .data([needlePercent])
      .enter()
      .append('path')
      .attr('d', d =>
        scales.lineRadial([
          [0, 0],
          [scales.needleScale(d), radii.outer_tick]
        ])
      )
      .attr('stroke', properties.needle_color)
      .attr('stroke-width', 6)
      .attr('stroke-linecap', 'round');

    gauge
      .select('g.needle')
      .append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', radii.cap)
      .attr('stroke', properties.needle_color)
      .attr('stroke-width', 6)
      .style('fill', 'white');

    return svg;
  };

  GaugeChart.create = function(element) {
    const svg = this.draw().node();
    element.append(svg);
  };

  GaugeChart.reRender = function(element) {
    this.remove(element);
    this.create(element);
  };

  GaugeChart.remove = function(element) {
    d3.select(element)
      .selectAll('svg')
      .remove();
  };

  return GaugeChart;
}

export default GaugeChart;
